
import { computed, defineComponent, onMounted, reactive, toRaw, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// constants
import { PERIODS } from '@/core/constants/periods.constants'

// composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { DataState } from '@/core/classes/DataState.class'
import { PeriodT } from '@/core/types/companies.types'
import { orders } from '@/core/realisations/orders.realisation'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        TheButton,
    },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        period: {
            type: Number,
            default: null,
        },
        date: {
            type: String,
            default: null,
        },
        orderId: {
            type: Number,
            required: true,
        },
        statusId: {
            type: Number,
            required: true,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_selected: false,
            is_loading: false,
            date: null as string | null,
            period: null as { id: number; name: string } | null,
            periodId: null as number | null,
        })

        const data_state = reactive({
            periods: new DataState<PeriodT>({}),
        })

        const min_date = computed(() => {
            const date = new Date(Date.now())
            const hours = date.getHours()

            if (hours >= 14) {
                date.setDate(date.getDate() + 1)
            }

            return date.toISOString().split('T')[0]
        })

        const onPeriodSelected = (periodId: number) => {
            const { findObjectByValue } = useHelpers()
            const period = findObjectByValue('id', periodId, PERIODS)

            if (!period) return
            state.period = period
        }

        const emitSubmit = async () => {
            if (!state.period || state.is_loading) return

            try {
                state.is_loading = true

                const payload = {
                    order_id: props.orderId,
                    new_date: state.date as string,
                    for_all_invoices: 1,
                    periodId: state.periodId,
                }
                await dispatcher.changeOrderTakeDate(payload)

                state.is_selected = true

                emit('submit', state.date)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При обновлении даты отправки произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadPeriods = async () => {
            data_state.periods.pagination.current_page = 1

            try {
                data_state.periods.is_loading = true
                const response = await orders.getPeriods()
                data_state.periods.list = response.data

                data_state.periods.pagination.total_pages = 1

                data_state.periods.is_failed = false
                data_state.periods.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.periods.failure.catchError(error)

                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_failed = true
                }
            } finally {
                if (!data_state.periods.failure.error_state.is_cancel) {
                    data_state.periods.is_loading = false
                }
            }
        }

        const emitReset = () => {
            state.is_selected = false
            state.date = null

            emit('reset')
        }

        const isCreatedStatus = computed(() => props.statusId === 1)

        watch(
            () => props.opened,
            (opened) => {
                if (!opened) {
                    state.is_selected = false
                    state.date = null
                    state.periodId = null
                    state.period = null
                }
            }
        )

        watch(
            () => props.date,
            (date) => {
                if (date) {
                    state.date = date

                    if (state.periodId) {
                        state.is_selected = true
                    }
                }
            }
        )

        watch(
            () => props.period,
            (periodId) => {
                if (periodId) {
                    state.periodId = periodId

                    const { findObjectByValue } = useHelpers()
                    const period = findObjectByValue('id', periodId, PERIODS)

                    if (!period) return
                    state.period = period

                    if (state.date) {
                        state.is_selected = true
                    }
                }
            }
        )

        onMounted(loadPeriods)

        return {
            state,
            min_date,
            data_state,
            emitSubmit,
            emitReset,
            onPeriodSelected,
            isCreatedStatus,
            loadPeriods,
        }
    },
})
